import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Paper,
  IconButton,
  ListSubheader,
  Autocomplete
} from '@mui/material';
import {
  Edit,
  ExpandMore,
  AccountTree,
  TrendingUp,
  Security,
  Assessment,
  Layers,
  Settings,
  Visibility,
  AttachMoney,
  Gavel,
  Description
} from '@mui/icons-material';
import { api as axios } from '../../api';
import url from '../../serverUrl';
import { logoWrapper } from '../../utils';
import TreasuryLockStatusFields from './TreasuryLockStatusFields';

import {
  ISSUER_CODES,
  COMPOSITION_TYPE_CODES,
  LIQUIDITY_TYPE_CODES,
  ASSET_TYPE_CODES,
  COLLATERALIZATION_LEVEL_TYPE_CODES,
  REBALANCING_TYPE_CODES,
  COMPLIANCE_TYPE_CODES,
  GEOGRAPHIC_REGION_CODES,
  RISK_PROFILE_CODES,
  FEE_TYPE_CODES,
  MECHANISM_TYPE_CODES,
  TRANSPARENCY_TYPE_CODES,
  WEIGHT_TYPE_CODES,
  STRATEGY_TYPE_CODES,
  TREASURY_MGMT_CODES,
  TREASURY_KEY_CODES
} from './dbtDictionary';
const normalizeArrayField = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

function EditDBTToken({ token, open, setOpen }) {
  /* ===================== STATE ===================== */
  const [form, setForm] = useState(token);
  const [status, setStatus] = useState(token?.status ?? false);
  const [logo, setLogo] = useState(token?.logo ?? '');

  /* ===================== EFFECTS ===================== */
  useEffect(() => {
    setForm(token);
    setStatus(token?.status ?? false);
    setLogo(token?.logo ?? '');
  }, [token]);

  /* ===================== HELPER FUNCTIONS ===================== */

  // Helper function to get label from code
  const getLabelFromCode = (code, codeArray) => {
    if (!code) return '';
    const item = codeArray.find((c) => c.code === code);
    return item ? item.label : '';
  };

  // Helper function to get code from label
  const labelToCode = (label, codeArray) => {
    if (!label) return '';
    const item = codeArray.find((c) => c.label === label);
    return item ? item.code : '';
  };

  /* ===================== HANDLERS ===================== */
  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handleMultiSelectChange = (field) => (event, newValue) => {
    setForm({ ...form, [field]: newValue });
  };

  const saveToken = () => {
    // Convert codes to labels before sending
    const dataToSend = {
      ...form,
      status: status ? 1 : 0,

      // ensure arrays are clean
      issuers: form.issuers ?? [],
      asset_types: form.asset_types ?? []
    };

    axios
      .put(`${url}/crypto/updateToken`, dataToSend)
      .then((res) => alert(res.data.message))
      .catch((err) => alert(JSON.stringify(err)));
  };

  const uploadLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fd = new FormData();
    fd.append('image', file);
    fd.append('fileName', file.name);

    axios.post(`${url}/hootdex/token-logo-upload`, fd).then((res) => {
      if (res.data.status === 'ok') {
        const img = `${url}/hootdex/images/${res.data.fileName}`;
        setLogo(img);
        setForm({ ...form, logo: img });
      }
    });
  };
  const ensureCode = (value, dictionary) => {
    if (!value || !Array.isArray(dictionary)) return '';

    // Case 1: already a valid code
    const isCode = dictionary.some((item) => item.code == value);
    if (isCode) return value;

    // Case 2: value is a label → map to code
    const labelMatch = dictionary.find((item) => item.label == value);
    if (labelMatch) return labelMatch.code;

    // Case 3: unknown / corrupted value
    console.warn('Unknown enum value:', value);
    return '';
  };

  useEffect(() => {
    if (!token) return;

    setForm({
      ...token,
      issuers: normalizeArrayField(token.issuers),

      composition_type_code: ensureCode(
        token.composition_type_code,
        COMPOSITION_TYPE_CODES
      ),
      liquidity_type_code: ensureCode(
        token.liquidity_type_code,
        LIQUIDITY_TYPE_CODES
      ),
      collateralization_level_code: ensureCode(
        token.collateralization_level_code,
        COLLATERALIZATION_LEVEL_TYPE_CODES
      ),
      rebalancing_type_code: ensureCode(
        token.rebalancing_type_code,
        REBALANCING_TYPE_CODES
      ),
      compliance_type_code: ensureCode(
        token.compliance_type_code,
        COMPLIANCE_TYPE_CODES
      ),
      region_code: ensureCode(token.region_code, GEOGRAPHIC_REGION_CODES),
      risk_profile_code: ensureCode(
        token.risk_profile_code,
        RISK_PROFILE_CODES
      ),
      fee_type_code: ensureCode(token.fee_type_code, FEE_TYPE_CODES),
      mechanism_type_code: ensureCode(
        token.mechanism_type_code,
        MECHANISM_TYPE_CODES
      ),
      transparency_type_code: ensureCode(
        token.transparency_type_code,
        TRANSPARENCY_TYPE_CODES
      ),
      weight_type_code: ensureCode(token.weight_type_code, WEIGHT_TYPE_CODES),
      strategy_type_code: ensureCode(
        token.strategy_type_code,
        STRATEGY_TYPE_CODES
      ),
      treasury_mgmt_code: ensureCode(
        token.treasury_mgmt_code,
        TREASURY_MGMT_CODES
      )
    });

    setStatus(Boolean(token.status));
    setLogo(token.logo ?? '');
  }, [token]);

  /* ===================== JSX ===================== */
  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ bgcolor: 'primary.dark', color: 'white', py: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" fontWeight={600}>
            Edit DBT Token
          </Typography>
          <Chip
            label={status ? 'Active' : 'Inactive'}
            color={status ? 'success' : 'default'}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#f8f9fa', p: 0 }}>
        <Box p={3}>
          {/* Logo & Status Section */}
          <Paper
            elevation={0}
            sx={{ p: 3, mb: 3, bgcolor: 'white', borderRadius: 2 }}
          >
            <Box display="flex" alignItems="center" gap={3}>
              <Box position="relative">
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '2px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fafafa'
                  }}
                >
                  <img
                    src={logoWrapper(logo, token)}
                    width={100}
                    alt={form?.name}
                    style={{ objectFit: 'contain' }}
                  />
                </Box>
                <input hidden id="dbt-logo" type="file" onChange={uploadLogo} />
                <label htmlFor="dbt-logo">
                  <IconButton
                    component="span"
                    size="small"
                    sx={{
                      position: 'absolute',
                      bottom: 5,
                      right: 5,
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </label>
              </Box>

              <Box flex={1}>
                <Typography variant="h6" gutterBottom>
                  {form?.name || 'DBT Token'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Symbol: {form?.symbol || 'N/A'}
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mt={2}>
                  <Typography variant="body2" fontWeight={500}>
                    Status:
                  </Typography>
                  <Switch
                    checked={status}
                    onChange={(e) => setStatus(e.target.checked)}
                  />
                  <Chip
                    label={status ? 'Active' : 'Inactive'}
                    color={status ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* ========== ACCORDION SECTIONS ========== */}

          {/* Identity Section */}
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Description color="primary" />
                <Typography fontWeight={600}>Identity Information</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  fullWidth
                  label="DBT Name"
                  value={form?.name ?? ''}
                  onChange={handleChange('name')}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Symbol"
                  value={form?.symbol ?? ''}
                  onChange={handleChange('symbol')}
                  size="small"
                />
                <AutocompleteField
                  label="Issuers"
                  value={form?.issuers ?? []}
                  onChange={handleMultiSelectChange('issuers')}
                  options={ISSUER_CODES}
                  multiple
                  freeSolo
                  size="small"
                />
                <TreasuryLockStatusFields tokenType="DAT" tokenId={form?.id} />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Composition & Asset Types Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Layers color="primary" />
                <Typography fontWeight={600}>
                  Composition & Asset Types
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Composition Type"
                  value={form?.composition_type_code}
                  onChange={handleChange('composition_type_code')}
                  options={COMPOSITION_TYPE_CODES}
                  grouped={true}
                  size="small"
                />
                <AutocompleteField
                  label="Underlying Asset Types"
                  value={form?.asset_types ?? []}
                  onChange={handleMultiSelectChange('asset_types')}
                  options={ASSET_TYPE_CODES}
                  multiple
                  size="small"
                />
                <SelectBlock
                  label="Collateralization Level"
                  value={form?.collateralization_level_code}
                  onChange={handleChange('collateralization_level_code')}
                  options={COLLATERALIZATION_LEVEL_TYPE_CODES}
                  grouped={true}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Weighting & Rebalancing Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <AccountTree color="primary" />
                <Typography fontWeight={600}>
                  Weighting & Rebalancing
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Weight Type"
                  value={form?.weight_type_code}
                  onChange={handleChange('weight_type_code')}
                  options={WEIGHT_TYPE_CODES}
                  grouped={true}
                  size="small"
                />
                <SelectBlock
                  label="Rebalancing Type"
                  value={form?.rebalancing_type_code}
                  onChange={handleChange('rebalancing_type_code')}
                  options={REBALANCING_TYPE_CODES}
                  grouped={true}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Strategy & Risk Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingUp color="primary" />
                <Typography fontWeight={600}>
                  Strategy & Risk Profile
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Strategy Type"
                  value={form?.strategy_type_code}
                  onChange={handleChange('strategy_type_code')}
                  options={STRATEGY_TYPE_CODES}
                  grouped={true}
                  size="small"
                />
                <SelectBlock
                  label="Risk Profile"
                  value={form?.risk_profile_code}
                  onChange={handleChange('risk_profile_code')}
                  options={RISK_PROFILE_CODES}
                  grouped={true}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Liquidity & Trading Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Assessment color="primary" />
                <Typography fontWeight={600}>Liquidity & Trading</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Liquidity Type"
                  value={form?.liquidity_type_code}
                  onChange={handleChange('liquidity_type_code')}
                  options={LIQUIDITY_TYPE_CODES}
                  grouped={true}
                  size="small"
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Current Price"
                  value={form?.current_price ?? ''}
                  onChange={handleChange('current_price')}
                  size="small"
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Volume (24h)"
                  value={form?.volume ?? ''}
                  onChange={handleChange('volume')}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Fees & Economics Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <AttachMoney color="primary" />
                <Typography fontWeight={600}>Fees & Economics</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Fee Type"
                  value={form?.fee_type_code}
                  onChange={handleChange('fee_type_code')}
                  options={FEE_TYPE_CODES}
                  grouped={true}
                  size="small"
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Management Fee (%)"
                  value={form?.management_fee ?? ''}
                  onChange={handleChange('management_fee')}
                  size="small"
                  helperText="Annual management fee percentage"
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Performance Fee (%)"
                  value={form?.performance_fee ?? ''}
                  onChange={handleChange('performance_fee')}
                  size="small"
                  helperText="Performance-based fee percentage"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Creation/Redemption Mechanism Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Settings color="primary" />
                <Typography fontWeight={600}>
                  Creation/Redemption Mechanism
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Mechanism Type"
                  value={form?.mechanism_type_code}
                  onChange={handleChange('mechanism_type_code')}
                  options={MECHANISM_TYPE_CODES}
                  grouped={true}
                  size="small"
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Creation Unit"
                  value={form?.min_creation_unit ?? ''}
                  onChange={handleChange('min_creation_unit')}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Transparency & Reporting Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Visibility color="primary" />
                <Typography fontWeight={600}>
                  Transparency & Reporting
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Transparency Type"
                  value={form?.transparency_type_code}
                  onChange={handleChange('transparency_type_code')}
                  options={TRANSPARENCY_TYPE_CODES}
                  grouped={true}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Treasury Management Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Security color="primary" />
                <Typography fontWeight={600}>Treasury Management</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Treasury Management Type"
                  value={form?.treasury_mgmt_code}
                  onChange={handleChange('treasury_mgmt_code')}
                  options={TREASURY_MGMT_CODES}
                  grouped={true}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Compliance & Region Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Gavel color="primary" />
                <Typography fontWeight={600}>Compliance & Region</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Compliance Type"
                  value={form?.compliance_type_code}
                  onChange={handleChange('compliance_type_code')}
                  options={COMPLIANCE_TYPE_CODES}
                  grouped={true}
                  size="small"
                />
                <SelectBlock
                  label="Region"
                  value={form?.region_code}
                  onChange={handleChange('region_code')}
                  options={GEOGRAPHIC_REGION_CODES}
                  grouped={true}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Description Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Description color="primary" />
                <Typography fontWeight={600}>Additional Information</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Description"
                value={form?.description ?? ''}
                onChange={handleChange('description')}
                helperText="Detailed description of the DBT offering"
                size="small"
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ bgcolor: 'white', px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}
      >
        <Button onClick={() => setOpen(false)} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={saveToken}
          sx={{
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
            px: 4
          }}
        >
          Save DBT Token
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ===================== HELPER COMPONENTS ===================== */

const SelectBlock = ({
  label,
  value,
  onChange,
  options,
  grouped = false,
  size = 'medium'
}) => {
  if (!grouped) {
    return (
      <FormControl fullWidth size={size}>
        <InputLabel>{label}</InputLabel>
        <Select value={value ?? ''} label={label} onChange={onChange}>
          {options?.map((o) => (
            <MenuItem key={o.code} value={o.code}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  // Group options by category for better organization
  const groupedOptions = options.reduce((acc, option) => {
    const category = option.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(option);
    return acc;
  }, {});

  return (
    <FormControl fullWidth size={size}>
      <InputLabel>{label}</InputLabel>
      <Select value={value ?? ''} label={label} onChange={onChange}>
        {Object.entries(groupedOptions)?.map(([category, items]) => [
          <ListSubheader
            key={`header-${category}`}
            sx={{ fontWeight: 600, color: 'primary.main' }}
          >
            {category}
          </ListSubheader>,
          ...items?.map((o) => (
            <MenuItem key={o.code} value={o.code} sx={{ pl: 4 }}>
              <Typography variant="body2">{o.label}</Typography>
            </MenuItem>
          ))
        ])}
      </Select>
    </FormControl>
  );
};

const AutocompleteField = ({
  label,
  value,
  onChange,
  options,
  multiple = false,
  freeSolo = false,
  size = 'medium'
}) => {
  const safeValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value ?? '';

  return (
    <Autocomplete
      multiple={multiple}
      freeSolo={freeSolo}
      size={size}
      options={Array.isArray(options) ? options.map((o) => o.label) : []}
      value={safeValue}
      onChange={onChange}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={label} />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            label={option}
            {...getTagProps({ index })}
            size="small"
            color="primary"
            variant="outlined"
          />
        ))
      }
    />
  );
};

export default React.memo(EditDBTToken);
